import type { SubcontractorRow } from '@/types'

/**
 * Subcontractors for ONE service tab (Asphalt, Concrete, Grading, Seal Coat,
 * Striping), shown as the last section of that tab, after Miscellaneous.
 *
 * Replaces the old standalone Subcontractors tab. Each entry is tied to the service
 * it's entered under via `section`, so there's no Service Type field; and its cost
 * + bonds become that service's "Subcontractors" row in Final Bid Totals, where the
 * markup is set like any other category — so there's no Markup % field either.
 *
 * CONTROLLED, and every service shares ONE list (`estimate.subcontractorsData`).
 * This component gets the whole list, shows only its own service's entries, and
 * hands the whole list back on every change. Edits apply in place, by id, so other
 * services' entries and legacy entries are never touched or reordered — order
 * matters for the legacy group, whose markup in Final Bid Totals is positional. With
 * no private copy of the list, switching tabs can't drop an edit or overwrite
 * another service's (the old panel kept local state and needed an unmount flush).
 */

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
)

const fmt = (v: number) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const selectAll = (e: React.MouseEvent<HTMLInputElement>) =>
  (e.target as HTMLInputElement).select()

let _nextId = 1
function nextId() {
  return `sub-${Date.now()}-${_nextId++}`
}

/** A new entry for `section`. markupPercent/serviceType stay 0/'' — legacy-only fields. */
export function newServiceSubcontractor(section: string): SubcontractorRow {
  return {
    id: nextId(),
    company: '',
    quoteNumber: '',
    cost: 0,
    markupPercent: 0,
    bonds: 0,
    description: '',
    serviceType: '',
    section,
  }
}

type EditableField = 'company' | 'quoteNumber' | 'cost' | 'bonds' | 'description'

interface ServiceSubcontractorsSectionProps {
  /** The service tab this section belongs to: asphalt, concrete, grading, seal-coat, striping. */
  section: string
  /** EVERY subcontractor entry on the estimate — all services plus legacy. */
  allRows: SubcontractorRow[]
  /** Receives the FULL updated list. Only this service's entries ever change. */
  onChange: (allRows: SubcontractorRow[]) => void
}

export function ServiceSubcontractorsSection({ section, allRows, onChange }: ServiceSubcontractorsSectionProps) {
  const rows = allRows.filter((r) => r.section === section)

  const handleAdd = () => onChange([...allRows, newServiceSubcontractor(section)])

  const handleDelete = (id: string) => onChange(allRows.filter((r) => r.id !== id))

  const handleFieldChange = (id: string, field: EditableField, value: string | number) =>
    onChange(allRows.map((r) => (r.id === id ? { ...r, [field]: value } : r)))

  const costTotal = rows.reduce((s, r) => s + (r.cost || 0), 0)
  const bondsTotal = rows.reduce((s, r) => s + (r.bonds || 0), 0)

  return (
    <div className="sub-panel svc-sub-panel">
      <div className="sub-header">
        <div className="sub-header-left">
          <UsersIcon />
          <span className="sub-title">Subcontractors</span>
          <span className="svc-sub-hint">Markup is set in Final Bid Totals</span>
        </div>
        <button className="sub-add-btn" onClick={handleAdd}>
          <PlusIcon />
          <span>Add Subcontractor</span>
        </button>
      </div>

      {rows.length > 0 ? (
        <div className="sub-table-wrap">
          <table className="sc-table sub-table">
            <thead>
              <tr>
                <th className="sub-col-company">SUBCONTRACTOR COMPANY</th>
                <th className="sub-col-quote">QUOTE #</th>
                <th className="sub-col-cost">COST</th>
                <th className="sub-col-bonds">BONDS</th>
                <th className="sub-col-desc">DESCRIPTION OF WORK</th>
                <th className="sub-col-total">TOTAL</th>
                <th className="sub-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      className="sc-input"
                      type="text"
                      value={row.company}
                      placeholder="Company name"
                      aria-label="Subcontractor company"
                      onChange={(e) => handleFieldChange(row.id, 'company', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="sc-input"
                      type="text"
                      value={row.quoteNumber}
                      placeholder="Quote #"
                      aria-label="Quote number"
                      onChange={(e) => handleFieldChange(row.id, 'quoteNumber', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="sc-input sub-num"
                      type="number"
                      value={row.cost || ''}
                      placeholder="0.00"
                      aria-label="Cost"
                      onClick={selectAll}
                      onChange={(e) => handleFieldChange(row.id, 'cost', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      className="sc-input sub-num"
                      type="number"
                      value={row.bonds || ''}
                      placeholder="0.00"
                      aria-label="Bonds"
                      onClick={selectAll}
                      onChange={(e) => handleFieldChange(row.id, 'bonds', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      className="sc-input"
                      type="text"
                      value={row.description}
                      placeholder="Description of work"
                      aria-label="Description of work"
                      onChange={(e) => handleFieldChange(row.id, 'description', e.target.value)}
                    />
                  </td>
                  <td className="sub-row-total">{fmt((row.cost || 0) + (row.bonds || 0))}</td>
                  <td>
                    <button className="sc-icon-btn" aria-label="Remove subcontractor" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 1 && (
              <tfoot>
                <tr className="sub-totals-row">
                  <td colSpan={2} className="sub-totals-label">Totals</td>
                  <td className="sub-totals-val">{fmt(costTotal)}</td>
                  <td className="sub-totals-val">{fmt(bondsTotal)}</td>
                  <td></td>
                  <td className="sub-totals-val"><span className="sc-foot-total">{fmt(costTotal + bondsTotal)}</span></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        <div className="sub-empty">
          <UsersIcon />
          <p>No subcontractors on this service</p>
          <button className="sub-empty-btn" onClick={handleAdd}>
            <PlusIcon />
            Add Subcontractor
          </button>
        </div>
      )}
    </div>
  )
}
